<template>
  <div
    v-if="!isArchived"
    class="flex"
    :class="isModelSelectorCard ? 'flex-col text-right items-end leading-tight gap-1' : 'flex-wrap items-center'"
  >
    <template v-if="isCustomizationPrice">
      <span qaid="customization-price" class="actual-price">
        {{ customizationPrice }}
      </span>
    </template>
    <template v-else>
      <comparable-value-drawer
        v-show="isComparableValueDrawerOpen"
        :is-drawer-open="isComparableValueDrawerOpen"
        @close="setIsComparableValueDrawerOpen(false)"
      />
      <span v-if="displayPrice" id="priceRange" class="flex flex-wrap items-center">
        <input id="product-display-price" type="hidden" :value="displayPrice" />
        <span qaid="actual-price" class="actual-price" :class="{salePrice: price.displayPriceValue.isSale}">{{
          displayPrice
        }}</span>
        <template v-if="isB2CWithoutOldPrice && isShowingCompValue">
          <span qaid="retail-msrp-text" class="text-xs text-gray-70 font-body ml-1">
            {{ $t('text.msrp') }}
          </span>
          <pdl-button
            v-if="isValidLangForComparableValue"
            tertiary
            :icon="'info_outline'"
            small
            class="comparable-value-tooltip"
            :icon-only="true"
            :label="$t('text.msrp')"
            :aria-expanded="isComparableValueDrawerOpen"
            aria-haspopup="dialog"
            aria-controls="comparable-value-modal"
            @click="setIsComparableValueDrawerOpen(true)"
          />
        </template>
        <span v-if="price.excludeTaxes && isConsumerFriendlyMode" class="sr-only">{{
          $t('text.buyingZone.salePrice')
        }}</span>
        <span v-if="price.excludeTaxes && isConsumerFriendlyMode" id="excludeTaxText" class="exclude-tax">
          {{ $t('product.price.excludeTax') }}
        </span>
      </span>

      <template v-if="price && price.displayPriceValue.oldPrice">
        <span qaid="original-price" class="text-gray-70 line-through ml-1">{{ price.displayPriceValue.oldPrice }}</span>
        <template v-if="isShowingCompValue">
          <span v-if="!b2b" class="text-xs text-gray-70 font-body ml-1" qaid="retail-msrp-text">{{
            $t('text.msrp')
          }}</span>
          <span class="sr-only">{{ $t('text.buyingZone.originalPrice') }}</span>
          <pdl-button
            v-if="isValidLangForComparableValue"
            tertiary
            :icon="'info_outline'"
            small
            class="comparable-value-tooltip"
            :icon-only="true"
            :label="$t('text.msrp')"
            :aria-expanded="isComparableValueDrawerOpen"
            aria-haspopup="dialog"
            aria-controls="comparable-value-modal"
            @click="setIsComparableValueDrawerOpen(true)"
          />
        </template>
      </template>
      <span v-if="b2b" class="ml-1 text-sm">
        <span v-if="isConsumerFriendlyMode" :qaid="qaidValueRetail">{{ $t('text.suggestedRetail') }}</span>
        <span v-else :qaid="qaidValueRetail">{{ $t('text.retailerCost') }}</span>
      </span>

      <pdp-product-price-tooltip
        v-if="b2b"
        v-show="!isConsumerFriendlyMode"
        :price="price"
        @min-qty-change="updateMinQty"
      >
      </pdp-product-price-tooltip>

      <template v-if="isRestrictedToRetailer && isTrekUCheckoutEnabled">
        <pdl-label v-if="trekUCompetencyLevel" qaid="pdp-treku-competency-label" class="ml-1" kind="positive">{{
          trekUCompetencyLevelTruncated
        }}</pdl-label>
        <pdl-label v-else class="pl-5 ml-1" qaid="pdp-treku-no-competency-label" kind="important">{{
          $t('text.buyingZone.B2B.trekU.noDiscount')
        }}</pdl-label>
      </template>
    </template>
    <div v-if="isShowing30DayPrice" qaid="best-30-day-price" class="text-xs font-light text-gray-70 w-full -mt-25">
      {{ $t('product.price.best30', [price.best30DayPrice.formatted]) }}
    </div>
  </div>
</template>

<script>
import {mapState, mapGetters} from 'vuex';
import get from 'lodash/get';
import PdpProductPriceTooltip from '@/components/containers/pdp/b2b/PdpProductPriceTooltip';
import {PdlLabel} from '@pedal/pdl-label';
import {PdlButton} from '@pedal/pdl-button';
import ComparableValueDrawer from '@/components/drawer/pricing/ComparableValueDrawer.vue';

export default {
  name: 'ProductPrice',
  components: {PdpProductPriceTooltip, PdlLabel, PdlButton, ComparableValueDrawer},
  props: {
    isArchived: {
      type: Boolean,
      default: false,
    },
    variantCode: {
      type: String,
      default: '',
      required: true,
    },
    isShowingCompValue: {
      type: Boolean,
      default: true,
    },
    isUpdatableByCustomization: {
      type: Boolean,
      default: false,
    },
    isModelSelectorCard: {
      type: Boolean,
      default: false,
    },
    isTrekUCheckoutEnabled: {
      type: Boolean,
      default: false,
    },
    has30DayPriceDisplay: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isComparableValueDrawerOpen: false,
    };
  },
  computed: {
    ...mapState('backend', ['isConsumerFriendlyMode', 'b2b', 'language']),
    ...mapState('user', ['trekUCompetencyLevel']),
    ...mapState('productDetails', ['isCustomizationMode']),
    ...mapState('bikeCustomization', ['bikeData']),
    ...mapGetters('advancedOrdering', ['advancedOrderingMode']),
    ...mapGetters('checkout', ['isRestrictedToRetailer']),
    ...mapGetters('productDetails', ['getConsumerPriceForVariant', 'getRetailerPriceForVariant']),

    displayPrice() {
      if (this.isConsumerFriendlyMode && this.price?.displayPriceValue?.applePrice) {
        return this.price.displayPriceValue.applePrice;
      }
      return get(this.price?.displayPriceValue, this.advancedOrderingMode && this.b2b ? 'advancedValue' : 'value', '');
    },
    consumer() {
      return this.variantCode ? this.getConsumerPriceForVariant(this.variantCode) : null;
    },
    retailer() {
      return this.variantCode ? this.getRetailerPriceForVariant(this.variantCode) : null;
    },
    price() {
      if (!this.b2b) {
        return this.consumer;
      }
      return this.isConsumerFriendlyMode ? this.consumer : this.retailer;
    },
    qaidValueRetail() {
      return !this.isConsumerFriendlyMode ? 'retail.cost.text' : 'retail.suggested.text';
    },
    trekUCompetencyLevelTruncated() {
      return this.trekUCompetencyLevel?.split(' ')[0];
    },
    isB2CWithoutOldPrice() {
      return !this.b2b && this.price && !this.price.displayPriceValue.oldPrice;
    },
    isValidLangForComparableValue() {
      return ['en_US', 'en_CA', 'fr_CA'].includes(this.language);
    },
    isCustomizationPrice() {
      return this.isUpdatableByCustomization && this.isCustomizationMode && this.customizationPrice;
    },
    customizationPrice() {
      return this.bikeData?.calculatedAdvertised;
    },
    has30DayPrice() {
      return this.price?.best30DayPrice?.formatted;
    },
    isShowing30DayPrice() {
      return this.has30DayPriceDisplay && this.has30DayPrice;
    },
  },
  methods: {
    updateMinQty(quantity) {
      this.$emit('min-qty-change', quantity);
    },
    setIsComparableValueDrawerOpen(value = false) {
      this.isComparableValueDrawerOpen = value;
    },
  },
};
</script>

<style lang="scss" scoped>
.comparable-value-tooltip {
  color: var(--gray-70);
  padding: 0.25rem 0.25rem;

  &:hover {
    color: var(--gray-60);
  }
}
</style>
